import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Header.module.scss';
import logoImg from '../../screen/images/logo.png';
import { useUserSession } from '../../screen/Auth/userSession';
import { COMPANY_MENU } from '../../screen/Company/companyMenu';
import { WORKS_CATEGORIES } from '../../screen/Works/worksService';

// 메뉴 순서: 회사소개 → 포트폴리오 → 주문하기 → 주문조회 → 견적문의 → 자주묻는질문
// 전체 메뉴(드로어)에서 하위 메뉴가 없는 항목 — 상단 GNB와 같은 구성
const DRAWER_LINKS = [
  { to: '/order', title: '주문하기' },
  { to: '/lookup', title: '주문조회' },
  { to: '/quote', title: '견적문의' },
  { to: '/faq', title: '자주묻는질문' },
];

// 드로어용 선 아이콘
const ICON_PATHS = {
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  login: (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="m10 17 5-5-5-5" />
      <path d="M15 12H3" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  admin: (
    <>
      <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  chevron: <path d="m6 9 6 6 6-6" />,
  arrow: (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
};

function Icon({ name, className }: { name: keyof typeof ICON_PATHS; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
      {ICON_PATHS[name]}
    </svg>
  );
}

function Header() {
  const navigate = useNavigate();
  const [session, setSession] = useUserSession();
  const [scrolled, setScrolled] = useState(false);
  const [topbarHidden, setTopbarHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
      setTopbarHidden(window.scrollY > 40);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // 드로어가 열린 채로 화면이 1080px보다 넓어지면(햄버거 버튼이 사라지는 구간) 자동으로 닫음
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 1081px)');
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setMobileOpen(false);
        setOpenGroup(null);
      }
    };
    wide.addEventListener('change', onChange);
    return () => wide.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    // html 에 overflow-x: hidden 이 있어 body 만 잠그면 화면 스크롤이 잠기지 않음 → html 도 함께 잠금
    const value = mobileOpen ? 'hidden' : '';
    document.documentElement.style.overflow = value;
    document.body.style.overflow = value;
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setOpenGroup(null);
  };

  // 전체 메뉴(드로어)에서 하위 메뉴가 있는 항목 — 상단 GNB와 같은 구성
  const drawerGroups = [
    {
      key: 'company',
     
      title: '회사소개',
      items: COMPANY_MENU.map((m) => ({ to: m.to, label: m.label })),
    },
    {
      key: 'works',
      title: '포트폴리오',
      items: [
        { to: '/works', label: '전체 보기' },
        ...WORKS_CATEGORIES.map((c) => ({ to: `/works?cat=${c.id}`, label: c.label })),
      ],
    },
  ];

  const handleLogout = () => {
    setSession(null);
    closeMobile();
    navigate('/');
  };

  return (
    <>
      <div className={`${styles.topBar} ${topbarHidden ? styles.hidden : ''}`}>
        <div className={styles.topBarInner}>
          <ul className={styles.topMenu}>
            {session ? (
              <>
                <li>
                  <Link to="/mypage">마이페이지</Link>
                </li>
                <li>
                  <button type="button" onClick={handleLogout}>
                    로그아웃
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link to="/login">로그인</Link>
                </li>
                <li>
                  <Link to="/signup">회원가입</Link>
                </li>
              </>
            )}
            <li>
              <Link to="/admin/login">관리자</Link>
            </li>
          </ul>
        </div>
      </div>

      <header
        className={`${styles.header} ${scrolled ? styles.scrolled : ''} ${topbarHidden ? styles.topbarHidden : ''}`}
      >
        <div className={styles.inner}>
          <Link to="/" className={styles.logo} aria-label="G-WORKS 홈">
            <img src={logoImg} alt="G-WORKS" />
          </Link>

          <nav className={styles.gnbArea} aria-label="주요 메뉴">
            <ul className={styles.gnb}>
              <li>
                <Link to={COMPANY_MENU[0].to}>회사소개</Link>
                <ul className={styles.drop}>
                  {COMPANY_MENU.map((m) => (
                    <li key={m.section}>
                      <Link to={m.to}>{m.label}</Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <Link to="/works">포트폴리오</Link>
                <ul className={styles.drop}>
                  {WORKS_CATEGORIES.map((c) => (
                    <li key={c.id}>
                      <Link to={`/works?cat=${c.id}`}>{c.label}</Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <Link to="/order">주문하기</Link>
              </li>
              <li>
                <Link to="/lookup">주문조회</Link>
              </li>
              <li>
                <Link to="/quote">견적문의</Link>
              </li>
              <li>
                <Link to="/faq">자주묻는질문</Link>
              </li>
            </ul>
          </nav>

          <button
            type="button"
            className={`${styles.hamburger} ${mobileOpen ? styles.open : ''}`}
            aria-label="전체 메뉴 열기"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {/* Rendered via portal so `.header`'s backdrop-filter doesn't trap this
          position:fixed drawer inside its own (header-height-tall) box. */}
      {createPortal(
        <div
          className={`${styles.drawer} ${mobileOpen ? styles.open : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="전체 메뉴"
          aria-hidden={!mobileOpen}
        >
          <div className={styles.drawerHead}>
            <Link to="/" className={styles.logo} aria-label="G-WORKS 홈" onClick={closeMobile}>
              <img src={logoImg} alt="G-WORKS" />
            </Link>
            <button type="button" className={styles.drawerClose} aria-label="메뉴 닫기" onClick={closeMobile}>
              ✕
            </button>
          </div>

          <div className={styles.drawerBody}>
            {/* 하위 메뉴가 있는 항목: 아코디언 */}
            <div className={styles.drawerMenus}>
              {drawerGroups.map((g) => {
                const expanded = openGroup === g.key;
                return (
                  <div key={g.key} className={`${styles.group} ${expanded ? styles.expanded : ''}`}>
                    <button
                      type="button"
                      className={styles.groupHead}
                      aria-expanded={expanded}
                      onClick={() => setOpenGroup(expanded ? null : g.key)}
                    >
                      <span className={styles.groupTitle}>{g.title}</span>
                      <Icon name="chevron" className={styles.chevron} />
                    </button>
                    <div className={styles.groupPanel}>
                      <div className={styles.groupPanelInner}>
                        <div className={styles.sub}>
                          {g.items.map((item) => (
                            <Link key={item.to} to={item.to} onClick={closeMobile}>
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 하위 메뉴가 없는 항목: 바로가기 목록 */}
            <div className={styles.quick}>
              {DRAWER_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={styles.quickLink} onClick={closeMobile}>
                  <span className={styles.groupTitle}>{l.title}</span>
                  <Icon name="arrow" />
                </Link>
              ))}
            </div>
          </div>

          {/* 계정 바: 상단 바(topBar) 대신 계정 메뉴 — 메뉴 목록 아래, 견적문의 바로가기 위 */}
          <div className={styles.drawerAccount}>
            <div className={styles.drawerAccountInner}>
              {session ? (
                <>
                  <Link to="/mypage" onClick={closeMobile}>
                    <Icon name="user" />
                    마이페이지
                  </Link>
                  <button type="button" onClick={handleLogout}>
                    <Icon name="logout" />
                    로그아웃
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={closeMobile}>
                    <Icon name="login" />
                    로그인
                  </Link>
                  <Link to="/signup" onClick={closeMobile}>
                    <Icon name="userPlus" />
                    회원가입
                  </Link>
                </>
              )}
              <Link to="/admin/login" onClick={closeMobile}>
                <Icon name="admin" />
                관리자
              </Link>
            </div>
          </div>

          <div className={styles.drawerFoot}>
            <div className={styles.drawerFootInner}>
              <p>
                <b>고객센터</b> &nbsp; 053-216-4126 &nbsp;·&nbsp; gn-co@daum.net
              </p>
              <Link to="/quote" className={styles.btnDark} onClick={closeMobile}>
                견적문의 바로가기
              </Link>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export default Header;
